import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import Colors from '../../utils/color';
import {
  getLeftStackHeight,
  saveLeftStackHeight,
} from '../../utils/local-storage';
import {
  CARD_MAX_HEIGHT,
  CARD_MIN_HEIGHT,
} from '../Layout/LayoutPreferenceDialog';
import { useAppSelector } from '../../app/hooks';
import { HITL7_BOTTOM_SECTION_HEIGHT } from '../../utils/hitl3Spacing';
import { selectIsHitl7Enabled } from '../../redux/features/config/config.selector';
import CustomerPulse from '../CustomerPulse';
import LiveTranscriptCard from './LiveTranscriptCard';
import useLeftStackCollapse from './hooks/useLeftStackCollapse.hooks';
import useStackLayout from './hooks/useStackLayout.hooks';
import useAlignedStripPct from './hooks/useAlignedStripPct.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  leftPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  // The left pane is a flush stack (Customer Pulse + Live Transcript): no outer
  // frame or padding — the sections attach edge-to-edge and are separated only
  // by the hairline resize divider below.
  leftCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  // Wraps each card inside a vertical panel: fills the panel height so each
  // card's OWN internal scroll works. overflow hidden (not auto) — the cards
  // scroll internally now, so this wrapper must never show its own (outer)
  // scrollbar, which otherwise appeared beside a collapsed hug-height card.
  leftStackPane: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
  },
  // Hairline divider between the stacked left-pane cards (Live Transcript / AI
  // Copilot). The handle is a 1px rule with NO white gutter, so the card headers
  // (e.g. the AI Copilot header band) butt straight up against the line. The
  // drag hit area (a wider invisible ::before) and the grip pill (::after) live
  // in pseudo-elements so the visible line stays 1px while staying easy to grab.
  vResizeHandle: {
    position: 'relative',
    zIndex: 1,
    height: 1,
    flexShrink: 0,
    outline: 'none',
    backgroundColor: Colors.alto,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: spacing(-0.75),
      bottom: spacing(-0.75),
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
      height: 3,
      width: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, width 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      width: spacing(5),
    },
  },
}));

/**
 * HITL-1663: how far the 7.0 chip strip collapses, as a percentage of the left
 * column — one row of chips, not nothing.
 *
 * One row is ~42px: the tallest chip is the response dial at 26px (a 20px ring
 * plus its padding and border), inside the strip's 8px top and bottom inset.
 * The column is the viewport less the 64px header, so at the smallest screen
 * this release supports — 1024x768, ~704px of column — 42px is 6%. On a taller
 * screen 6% is a little more than one row, which is the right way to be wrong:
 * the strip is meant to show one row COMFORTABLY.
 *
 * Collapsing to 0 instead would mean a chip arriving has nowhere to appear, so
 * every first signal of an order would jolt the transcript as the panel opened.
 */
const HITL7_STRIP_COLLAPSED_PCT = 6;

interface LeftSidebarProps {
  // Per-agent storage key; the nested vertical stack persists under a suffix.
  paneStorageKey: string;
}

/**
 * LEFT SIDEBAR of the HITL console. One card holding a drag-to-resizable
 * vertical stack of Live Transcript (top) + Customer Pulse (bottom) (sizes
 * persist per agent via autoSaveId). The Pulse strip folds in the metrics, signal/reminder chips
 * and the AI co-pilot ask (formerly the separate Action Center card), so the
 * Transcript gets the bulk of the height. On an unsupervised AI task arrival the
 * left PANE widens to 50% (see usePaneLayout) — keeping menu + cart visible —
 * while supervised keeps the agent's saved pane sizes. Collapse-sync between each
 * card's chevron and its panel lives in useLeftStackCollapse.
 */
const LeftSidebar = ({ paneStorageKey }: LeftSidebarProps) => {
  const classes = useStyles();
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const { cpOpen, setCpOpen, ltOpen, setLtOpen, cpPanelRef, ltPanelRef } =
    useLeftStackCollapse();
  // Card-height persistence + live dialog control for this stack, mirroring the
  // horizontal panes (single source of truth = the dedicated height key).
  const { groupRef, reportSizes, persistOnDragEnd } = useStackLayout({
    region: 'left',
    getSaved: getLeftStackHeight,
    saveSaved: saveLeftStackHeight,
    staleAutoSaveKey: `react-resizable-panels:${paneStorageKey}:left-stack-3z`,
    restoreOnMount: true,
    min: CARD_MIN_HEIGHT,
    max: CARD_MAX_HEIGHT,
  });
  // HITL-1665: the strip rests on the same shared floor as the typing box and
  // the Cancel / Finish row, so the three bottom rules line up. Panels are sized
  // in PERCENT (react-resizable-panels v1 has no pixel sizes), so the token is
  // converted against this stack's own measured height.
  const stackRef = useRef<HTMLDivElement>(null);
  // NOT gated on savedOnMount: these are the panel's own default and collapsed
  // sizes, which is a different thing from the agent's saved split. Gating them
  // meant an agent who had ever dragged this stack got the old 25% / 6% sizes
  // back and never saw the alignment at all. Their saved split still wins where
  // it should — useStackLayout's restoreOnMount applies it over the default.
  const alignedStripPct = useAlignedStripPct({
    enabled: isHitl7,
    stackRef,
    targetHeight: HITL7_BOTTOM_SECTION_HEIGHT,
  });

  return (
    <div className={classes.leftPane}>
      <div className={classes.leftCard} ref={stackRef}>
        {/* Left-pane cards (Live Transcript on top, Customer Pulse strip below)
            are drag-to-resizable; heights persist per agent via the dedicated
            `hitl-layout-height-left` key (drag via persistOnDragEnd, dialog via
            Apply) — no autoSaveId, so there's a single source of truth. */}
        <PanelGroup direction="vertical" ref={groupRef} onLayout={reportSizes}>
          <Panel
            id="lt"
            order={1}
            ref={ltPanelRef}
            collapsible
            collapsedSize={7}
            defaultSize={75}
            minSize={15}
            onCollapse={() => setLtOpen(false)}
            onExpand={() => setLtOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <LiveTranscriptCard open={ltOpen} onOpenChange={setLtOpen} />
            </div>
          </Panel>
          <PanelResizeHandle
            className={classes.vResizeHandle}
            onDragging={persistOnDragEnd}
          />
          {/* collapsedSize is larger than the transcript's (7): the collapsed
              Pulse strip keeps its AI status band below the header, so it needs
              ~2x the height. Stays below minSize so collapse still snaps in.

              HITL-1663: under 7.0 there is no header band and no chevron to
              keep visible, and the status band moved to full console width in
              HITL-1660, so a collapsed strip shrinks to a single row of chips
              and the conversation takes the rest. CustomerPulse collapses
              itself whenever it has no chips to show; the 1px handle stays
              draggable, so the agent can always pull it back. */}
          {/* HITL-1665: with 7.0 on and no saved height, the strip rests at the
              footer's height so the three bottom rules align. Both the collapsed
              and the default size take it — the strip spends most of a session
              collapsed (CustomerPulse folds itself whenever it has no chips), so
              aligning only the expanded size would leave it misaligned exactly
              when the agent is looking at an idle console.

              This deliberately gives the strip more height than HITL-1663 left
              it (one chip row), which it took from the transcript. Reverting is
              a one-line change: drop alignedStripPct from these two props. */}
          <Panel
            id="cp"
            order={2}
            ref={cpPanelRef}
            collapsible
            collapsedSize={
              alignedStripPct ?? (isHitl7 ? HITL7_STRIP_COLLAPSED_PCT : 11)
            }
            defaultSize={alignedStripPct ?? 25}
            // The floor has to yield to the aligned size, or the alignment
            // silently fails on tall screens: the footer is a fixed ~114px, so
            // the percentage it maps to SHRINKS as the column grows, and on a
            // 1080p console it lands under 15 and gets clamped straight back to
            // a misaligned 15%.
            minSize={Math.min(15, alignedStripPct ?? 15)}
            onCollapse={() => setCpOpen(false)}
            onExpand={() => setCpOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <CustomerPulse open={cpOpen} onOpenChange={setCpOpen} />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
};

export default LeftSidebar;
